/**
 * Copyright © 2026 Adnuntius AS.
 */
import {DOCUMENT, Component, Inject, OnInit} from '@angular/core';
import {UIRouter} from '@uirouter/core';
import * as _ from 'lodash';

import template from './ad-transparency-preview-page.component.html';
import {PoliticalAdsResource} from '../../../components/api/resources/resource/political-ads.resource';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';

const EMPTY_TEMPLATE_MESSAGE = '<!-- Default ad transparency template could not be loaded. -->';

@Component({
  standalone: false,
  selector: 'adn-ad-transparency-preview-page',
  template
})
export class AdTransparencyPreviewPageComponent implements OnInit {
  networkTag = '';
  form = {
    lineItemId: '',
    publisherId: '',
    template: ''
  };
  error = '';
  loading = false;
  hasPreview = false;
  lastRenderedAt: Date | null = null;
  templateLoading = true;
  codemirrorReady = false;

  constructor(
    @Inject(DOCUMENT) private readonly document: any,
    @Inject(PoliticalAdsResource) private readonly PoliticalAds: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly uiRouter: UIRouter
  ) {
  }

  ngOnInit(): void {
    this.networkTag = this.LocalNetworkProfile.getNetworkTag();
    this.form.lineItemId = this.uiRouter.globals.params.lineItemId || '';
    this.form.publisherId = this.uiRouter.globals.params.publisherId || '';

    setTimeout(() => {
      this.codemirrorReady = true;
    }, 0);

    this.PoliticalAds.getHtmlTemplate().then((response: any) => {
      this.form.template = this.extractTemplate(response) || EMPTY_TEMPLATE_MESSAGE;
    }).catch((error: any) => {
      this.form.template = EMPTY_TEMPLATE_MESSAGE;
      this.error = _.get(error, ['data'], 'Unable to load the default template.');
    }).finally(() => {
      this.templateLoading = false;
      if (this.form.lineItemId && this.networkTag && this.form.template) {
        this.render();
      }
    });
  }

  render(): void {
    this.error = '';
    this.loading = true;

    this.PoliticalAds.renderHtml({
      n: this.networkTag,
      li: this.form.lineItemId,
      p: this.form.publisherId,
      template: this.form.template
    }).then((html: string) => {
      this.hasPreview = true;
      this.lastRenderedAt = new Date();
      this.writeIframe(html);
    }).catch((error: any) => {
      this.hasPreview = false;
      this.error = _.get(error, ['data'], 'Unable to render political ad preview.');
      this.writeIframe('');
    }).finally(() => {
      this.loading = false;
    });
  }

  private writeIframe(html: string): void {
    setTimeout(() => {
      const iframe = this.document.getElementById('politicalAdsPreviewFrame');
      if (!iframe) {
        return;
      }

      iframe.removeAttribute('src');
      iframe.setAttribute('srcdoc', html || '');
    });
  }

  private extractTemplate(response: any): string {
    if (_.isString(response)) {
      return response;
    }
    return _.get(response, 'template')
      || _.get(response, ['result', 'template'])
      || _.get(response, ['data', 'template'])
      || '';
  }
}
